import * as React from 'react';

import { useNewAddResourceMapping } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddResourceMapping';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import {
  ModelConceptClass,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import {
  isModelConceptClass,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const useAddMappingWithSelection = () => {
  const selection = useDiagramSelection();
  const defaultConnection = useDefaultPortalConnection();
  const handleNewAddMapping = useNewAddResourceMapping();

  const isAutoMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isAutoMappingSupported',
      defaultConnection
    )
  );

  const { classes, resources, isNewMappingEnabled } = React.useMemo(() => {
    const classes: ModelConceptClass[] = [];
    const resources: Resource[] = [];

    selection.forEach((item) => {
      if (isModelConceptClass(item.data)) {
        classes.push(item.data);
      } else if (isResource(item.data)) {
        resources.push(item.data);
      }
    }, {});

    const isAddMappingEnabledForSelection =
      isAutoMappingSupported && resources.length;

    return {
      classes,
      resources,
      isNewMappingEnabled: isAddMappingEnabledForSelection,
    };
  }, [isAutoMappingSupported, selection]);

  const handleAddMappingWithSelection = React.useCallback(() => {
    if (!isNewMappingEnabled) {
      return;
    }

    handleNewAddMapping({
      initialClassIris: classes.map((classData) => classData.value),
      initialResourceIds: resources.map((resourceData) => resourceData.value),
    });
  }, [classes, handleNewAddMapping, isNewMappingEnabled, resources]);

  return {
    isNewMappingEnabled,
    handleAddMappingWithSelection,
  };
};
